import { GetServerSideProps } from 'next'
import dynamic from 'next/dynamic'
import { SEO } from '~/components/seo'
import { env } from '~/config/env'
import {
  PageBySlugDocument,
  PageBySlugQuery,
  PageBySlugQueryVariables,
} from '~/graphql/page-by-slug'
import { withStripe } from '~/hocs/with-stripe'
import { initializeApollo } from '~/hooks/use-apollo'
import { FundraisingPage } from '~/pages/fundraising-page'
import { PageType } from '~/types/graphql'
import { isMissingPageError } from '~/utils/api-error'

const DonationPage = dynamic(import('~/pages/donation-page'))
const EventPage = dynamic(import('~/pages/event-page'))

type Props = {
  page: PageBySlugQuery['pageBySlug']
  hasDonations?: boolean
  fundraiser?: PageBySlugQuery['pageBySlug']['fundraisers'][0]
}

const PageRouter = ({ page, hasDonations, fundraiser }: Props) => {
  const renderPage = () => {
    if (page.type === PageType.DonationPage) {
      return <DonationPage page={page} fundraiser={fundraiser} />
    }

    if (page.type === PageType.Event) {
      return <EventPage page={page} hasDonations={hasDonations!} />
    }

    return <FundraisingPage page={page} hasDonations={hasDonations!} />
  }

  return (
    <>
      <SEO
        title={page.title}
        description={`Click here to support ${page.nonprofit.name}.`}
        image={page.type === PageType.FundraisingPage ? page.imageUrl! : null}
        url={page.pageUrl ?? `${env.baseUrl}/${page.slug}`}
        canonicalUrl={page.pageUrl}
        hideKindest
      />
      {renderPage()}
    </>
  )
}

export default withStripe(PageRouter)

export const getServerSideProps: GetServerSideProps<Props> = async context => {
  const apolloClient = initializeApollo()

  try {
    const { data } = await apolloClient.query<
      PageBySlugQuery,
      PageBySlugQueryVariables
    >({
      query: PageBySlugDocument,
      variables: {
        slug: context.query.slug as string,
      },
    })

    return {
      props: {
        page: data!.pageBySlug,
        hasDonations: data!.community.pageInfo.totalItems !== 0,
      },
    }
  } catch (error: any) {
    if (isMissingPageError(error)) {
      return { notFound: true }
    }

    throw error
  }
}
